// Estado global (Zustand). Persiste tema, selección favorita y la simulación de la
// llave en localStorage (clave 'm26-prefs') → queda guardado en el dispositivo.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ModelWeights } from '../api/client';

type ThemeName = 'dark' | 'light';

// Picks de la simulación: num de partido -> nombre del equipo elegido ganador.
export type BracketPicks = Record<number, string>;

interface AppState {
  theme: ThemeName;
  favorite: string | null;
  bracketPicks: BracketPicks;
  lastRoute: string; // última ruta visitada (para restaurar al volver)
  seenWelcome: boolean; // ya vio el aviso de bienvenida
  nick: string; // nombre/apodo elegido en el primer ingreso (asociado a la pubkey)
  tz: string; // zona horaria IANA elegida ('' = la del dispositivo)
  accent: string; // color principal personalizado (hex; '' = el del tema)
  adminKey: string; // clave validada que desbloquea el menú de estadísticas ('' = oculto)
  announcementsSeen: Record<string, number>; // id de aviso -> timestamp de la última vez mostrado
  predictAuto: boolean; // usar pesos del pronóstico calibrados con resultados
  predictWeights: ModelWeights | null; // pesos personalizados del usuario (null = no tocado)
  oracleTuningOpen: boolean; // ventana de "Ajuste del Oráculo" abierta
  introPending: boolean; // usuario nuevo: mostrar el "Acerca de" tras la bienvenida
  startRoute: string; // inicio al abrir: '' = default (Timba) · '/' = Hoy · otra ruta = esa
  timbaRoundCollapse: Record<string, boolean>; // fase de la llave -> override del usuario (true=colapsada). Ausente = default (abierta si hay partidos activos, colapsada si la fase terminó)
  toggleTheme: () => void;
  setFavorite: (team: string | null) => void;
  setBracketPicks: (picks: BracketPicks) => void;
  resetBracket: () => void;
  setLastRoute: (route: string) => void;
  setSeenWelcome: () => void;
  setNick: (nick: string) => void;
  setTz: (tz: string) => void;
  setAccent: (hex: string) => void;
  setAdminKey: (key: string) => void;
  clearAdmin: () => void;
  markAnnouncementSeen: (id: string) => void;
  setPredictAuto: (on: boolean) => void;
  setPredictWeights: (weights: ModelWeights) => void;
  resetPredictWeights: () => void;
  setOracleTuning: (open: boolean) => void;
  setIntroPending: (on: boolean) => void;
  setStartRoute: (route: string) => void;
  setTimbaRoundCollapse: (key: string, collapsed: boolean) => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      favorite: null,
      bracketPicks: {},
      lastRoute: '',
      seenWelcome: false,
      nick: '',
      tz: '',
      accent: '',
      adminKey: '',
      announcementsSeen: {},
      predictAuto: true,
      predictWeights: null,
      oracleTuningOpen: false,
      introPending: false,
      startRoute: '',
      timbaRoundCollapse: {},
      toggleTheme: () => set({ theme: get().theme === 'dark' ? 'light' : 'dark' }),
      setFavorite: (team) => set({ favorite: get().favorite === team ? null : team }),
      setBracketPicks: (picks) => set({ bracketPicks: picks }),
      resetBracket: () => set({ bracketPicks: {} }),
      setLastRoute: (route) => set({ lastRoute: route }),
      setSeenWelcome: () => set({ seenWelcome: true }),
      setNick: (nick) => set({ nick }),
      setTz: (tz) => set({ tz }),
      setAccent: (hex) => set({ accent: hex }),
      setAdminKey: (key) => set({ adminKey: key }),
      clearAdmin: () => set({ adminKey: '' }),
      markAnnouncementSeen: (id) =>
        set({ announcementsSeen: { ...get().announcementsSeen, [id]: Date.now() } }),
      // Editar pesos pasa a modo manual (apaga el auto). Reset vuelve a auto/base.
      setPredictAuto: (on) => set({ predictAuto: on }),
      setPredictWeights: (weights) => set({ predictAuto: false, predictWeights: weights }),
      resetPredictWeights: () => set({ predictWeights: null, predictAuto: true }),
      setOracleTuning: (open) => set({ oracleTuningOpen: open }),
      setIntroPending: (on) => set({ introPending: on }),
      setStartRoute: (route) => set({ startRoute: route }),
      setTimbaRoundCollapse: (key, collapsed) =>
        set({ timbaRoundCollapse: { ...get().timbaRoundCollapse, [key]: collapsed } }),
    }),
    { name: 'm26-prefs' }
  )
);
